import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ButtonComponent,
  DialogBodyDirective,
  DialogFooterComponent,
  DialogHeaderDirective,
  DialogRef,
  IconComponent,
  NebulaSearchCriteria,
  NebulaSmartTableComponent,
  NebulaSmartTableConfig,
  NebulaTableQuery,
} from 'nebula-ui-kit';
import { HttpService, PageResponse, TranslatePipe } from '@nebula-shared/data-access';
import { Subscription } from 'rxjs';
import { BuscadorMaestroConfig } from './buscador-maestro.models';

/**
 * Ventana de busqueda de maestros (shared-ui). Por dentro es una lib-smart-table: un solo armado
 * de criterios (paginacion 10/20/50, termino, filtros por columna y orden); la ventana suma los
 * fijos de la base, delante y con AND, y consulta el `page-response` del maestro.
 *
 * <p>Devuelve SIEMPRE un arreglo: [R] en seleccion unica, [R…] en multiple (acumulada por idKey
 * entre paginas y filtros), o null si se cancela.</p>
 */
@Component({
  selector: 'app-buscador-maestro',
  standalone: true,
  imports: [
    ButtonComponent,
    DialogBodyDirective,
    DialogFooterComponent,
    DialogHeaderDirective,
    IconComponent,
    NebulaSmartTableComponent,
    TranslatePipe,
  ],
  templateUrl: './buscador-maestro.component.html',
  styleUrls: ['./buscador-maestro.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuscadorMaestroComponent<R extends Record<string, unknown>> {
  /** Configuracion con que se abre. */
  readonly config = input.required<BuscadorMaestroConfig<R>>();

  private readonly http = inject(HttpService);
  private readonly ref = inject(DialogRef, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  protected readonly filas = signal<R[]>([]);
  protected readonly total = signal(0);
  protected readonly cargando = signal(false);
  protected readonly fallo = signal(false);
  /** Seleccion multiple acumulada por idKey, entre paginas y filtros. */
  protected readonly elegidos = signal(new Map<unknown, R>());
  protected readonly cuantos = computed(() => this.elegidos().size);

  private ultima?: NebulaTableQuery;
  private lectura?: Subscription;
  /** En multiple: lo que ya entro a lo elegido por ser el valor actual (entra una sola vez). */
  private readonly sembrados = new Set<unknown>();

  /**
   * Lo que pinta la grilla: cada registro con la presentacion de la configuracion, si la hay. La
   * grilla del kit marca sus filas por `id`; al maestro que no lo trae (usuarios, paises…) se le
   * pone, solo para pintar, el valor de su idKey.
   */
  protected readonly vista = computed<R[]>(() => {
    const { presentar, idKey } = this.config();
    return this.filas().map((fila) => {
      const pintada = presentar ? presentar(fila) : fila;
      return pintada['id'] === null || pintada['id'] === undefined
        ? ({ ...pintada, id: fila[idKey] } as R)
        : pintada;
    });
  });

  /** Filas de la pagina vigente que coinciden con el valor actual del campo. */
  private readonly actuales = computed<R[]>(() => {
    const actual = this.config().valorActual;
    if (!actual) return [];
    const valores = (Array.isArray(actual.valor) ? actual.valor : [actual.valor])
      .filter((v) => v !== null && v !== undefined && v !== '')
      .map((v) => String(v));
    if (!valores.length) return [];
    return this.filas().filter((fila) => valores.includes(String(fila[actual.clave] ?? '')));
  });

  /** La grilla de la ventana, construida con las columnas de la configuracion. */
  protected readonly tabla = computed<NebulaSmartTableConfig<R>>(() => {
    const cfg = this.config();
    return {
      columns: cfg.columnas,
      selectable: !!cfg.multiple,
      multiple: !!cfg.multiple,
      rowClickable: !cfg.multiple,
      // Densa como las tablas densas del ERP, y con su selector visible como en las listas.
      density: 'compact',
      densitySelector: true,
      pageSize: cfg.base.size ?? 10,
      pageSizeOptions: [10, 20, 50],
      initialSort: cfg.base.ordersBy?.[0]
        ? {
            column: cfg.base.ordersBy[0].column,
            direction: cfg.base.ordersBy[0].direction === 'DESC' ? 'desc' : 'asc',
          }
        : undefined,
    };
  });

  /**
   * Lo elegido, por el `id` con que la grilla marca sus casillas, para que al volver a una pagina
   * sigan marcadas.
   */
  protected readonly idsElegidos = computed(() => {
    const id = (r: R) => r['id'] ?? r[this.config().idKey];
    const ids = [...this.elegidos().values()].map(id);
    // En seleccion unica no hay acumulado: se marca la fila del valor actual del campo.
    if (!this.config().multiple) ids.push(...this.actuales().map(id));
    return ids;
  });

  /**
   * La grilla emitio su consulta: se suman los fijos de la base (delante, AND) y se consulta el
   * servidor. Una lectura nueva cancela la anterior.
   *
   * @param consulta la consulta de la grilla (page, size, searchsBy, ordersBy).
   */
  protected consultar(consulta: NebulaTableQuery): void {
    this.ultima = consulta;
    this.lectura?.unsubscribe();
    this.cargando.set(true);
    this.fallo.set(false);
    const cuerpo = {
      page: consulta.page,
      size: consulta.size,
      searchsBy: unirConFijos(this.config().base.searchsBy, consulta.searchsBy),
      ordersBy: consulta.ordersBy?.length ? consulta.ordersBy : (this.config().base.ordersBy ?? []),
    };
    this.lectura = this.http
      .postContent<PageResponse<R>>(this.config().endpoint, cuerpo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (pagina) => {
          // Un 200 sin pagina (cuerpo vacio o de otra forma) no es «sin resultados»: es un fallo,
          // y la ventana lo dice con su mensaje y el reintento en vez de quedarse en blanco.
          if (!pagina || !Array.isArray(pagina.content)) {
            this.filas.set([]);
            this.cargando.set(false);
            this.fallo.set(true);
            return;
          }
          this.filas.set(pagina.content);
          this.total.set(pagina.totalElements ?? 0);
          this.cargando.set(false);
          this.sembrarActuales();
        },
        error: () => {
          this.filas.set([]);
          this.cargando.set(false);
          this.fallo.set(true);
        },
      });
  }

  /** Vuelve a pedir la ultima consulta tras un error. */
  protected reintentar(): void {
    if (this.ultima) this.consultar(this.ultima);
  }

  /**
   * En multiple, el valor actual del campo entra a lo elegido la primera vez que aparece; si el
   * usuario lo desmarca, no vuelve a entrar al cambiar de pagina.
   */
  private sembrarActuales(): void {
    const cfg = this.config();
    if (!cfg.multiple) return;
    const nuevos = this.actuales().filter((fila) => !this.sembrados.has(fila[cfg.idKey]));
    if (!nuevos.length) return;
    const siguiente = new Map(this.elegidos());
    for (const fila of nuevos) {
      this.sembrados.add(fila[cfg.idKey]);
      siguiente.set(fila[cfg.idKey], fila);
    }
    this.elegidos.set(siguiente);
  }

  /**
   * El registro tal como llego del servidor para una fila de la grilla, que puede venir con la
   * presentacion aplicada.
   *
   * @param fila la fila de la grilla.
   * @returns el registro original de la pagina, o la misma fila si no esta.
   */
  private original(fila: R): R {
    const clave = this.config().idKey;
    return this.filas().find((f) => f[clave] === fila[clave]) ?? fila;
  }

  /** Seleccion unica: la fila elegida cierra la ventana. */
  protected elegirFila(fila: R): void {
    if (this.config().multiple) return;
    this.ref?.close([this.original(fila)]);
  }

  /**
   * Seleccion multiple: lo marcado en la pagina vigente reemplaza lo de esa pagina; lo de otras
   * paginas o filtros se conserva.
   *
   * @param marcados filas marcadas en la pagina vigente.
   */
  protected marcar(marcados: R[]): void {
    const clave = this.config().idKey;
    const siguiente = new Map(this.elegidos());
    for (const fila of this.filas()) siguiente.delete(fila[clave]);
    for (const fila of marcados) siguiente.set(fila[clave], this.original(fila));
    this.elegidos.set(siguiente);
  }

  /** Confirma la seleccion multiple. */
  protected aceptar(): void {
    const lista = [...this.elegidos().values()];
    this.ref?.close(lista.length ? lista : null);
  }

  /** Cierra sin elegir. */
  protected cancelar(): void {
    this.ref?.close(null);
  }
}

/**
 * Une los criterios fijos de la base con los de la grilla: los fijos van delante y con AND, y no
 * los quita ni los reemplaza un filtro de la grilla sobre la misma clave.
 *
 * @param fijos criterios de la base.
 * @param grilla criterios que compuso la grilla (termino, filtros, periodo).
 * @returns la lista de criterios del page-response.
 */
export function unirConFijos(
  fijos: NebulaSearchCriteria[],
  grilla: NebulaSearchCriteria[] | undefined,
): NebulaSearchCriteria[] {
  const delante = fijos.map((c) => ({ ...c, linkOperator: 'AND' as const }));
  return [...delante, ...(grilla ?? [])];
}
